import { useRef, useEffect, useCallback } from 'react';

/**
 * 全域共用的層級式瀏覽器返回機制。
 *
 * 每次開啟一個 dialog / lightbox 時呼叫 pushLayer(layerName)，
 * 關閉時呼叫 popLayer()。
 * 當使用者按下瀏覽器「上一頁」時，onPop 會收到被彈出的層級名稱，
 * 由呼叫端決定要關閉哪個視窗。
 *
 * @param onPop 收到被彈出的層級名稱（若層級堆疊已空則為 undefined）
 * @returns { pushLayer, popLayer, historyLayerRef }
 */
export function useLayeredHistory(onPop: (layer: string | undefined) => void) {
  const historyLayerRef = useRef<string[]>([]);

  // 透過 ref 持有最新的 onPop，避免 effect 重新訂閱
  const onPopRef = useRef(onPop);
  onPopRef.current = onPop;

  /** 新增一個歷史紀錄並追蹤層級名稱 */
  const pushLayer = useCallback((layer: string) => {
    if (typeof window === 'undefined') return;
    window.history.pushState(null, '', window.location.href);
    historyLayerRef.current = [...historyLayerRef.current, layer];
  }, []);

  /** 移除最上層並呼叫 window.history.back() */
  const popLayer = useCallback(() => {
    historyLayerRef.current = historyLayerRef.current.slice(0, -1);
    if (typeof window !== 'undefined') window.history.back();
  }, []);

  /** 統一 popstate 監聽，訂閱一次 */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePop = () => {
      const layers = historyLayerRef.current;
      const top = layers[layers.length - 1];
      historyLayerRef.current = layers.slice(0, -1);
      onPopRef.current(top);
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  return { pushLayer, popLayer, historyLayerRef };
}
